import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TeamMember } from '@/types/database';
import { toast } from 'sonner';

// Types for API responses
interface TeamMembersResponse {
  teamMembers: (TeamMember & {
    profiles: {
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      avatar_url: string | null;
    } | null;
  })[];
}

interface TeamMemberResponse {
  teamMember: TeamMember & {
    profiles: {
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      avatar_url: string | null;
    } | null;
  };
  message?: string;
}

interface UpdateTeamMemberRequest {
  memberId: string;
  data: {
    role?: string;
    permissions?: Record<string, boolean>;
    status?: string;
  };
}

interface JoinTeamRequest {
  invitationToken: string;
}

// API functions
export const teamApi = {
  // Get team members
  getTeamMembers: async (): Promise<TeamMembersResponse> => {
    const response = await fetch('/api/team', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch team members');
    }

    const result = await response.json();
    return result.data; // Extract data from the API response wrapper
  },

  // Update team member
  updateTeamMember: async ({ memberId, data }: UpdateTeamMemberRequest): Promise<TeamMemberResponse> => {
    const response = await fetch(`/api/team/${memberId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update team member');
    }

    return response.json();
  },

  // Remove team member
  removeTeamMember: async (memberId: string): Promise<{ message: string }> => {
    const response = await fetch(`/api/team/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove team member');
    }

    return response.json();
  },

  // Join team (accept invitation)
  joinTeam: async ({ invitationToken }: JoinTeamRequest): Promise<TeamMemberResponse> => {
    const response = await fetch('/api/team', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ invitationToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to join team');
    }

    return response.json();
  },
};

// React Query hooks
export function useTeamMembers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['team', 'members'],
    queryFn: teamApi.getTeamMembers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error.message?.includes('Unauthorized') || error.message?.includes('Forbidden')) {
        return false;
      }
      return failureCount < 2;
    },
    enabled: options?.enabled !== false,
  });
}

export function useUpdateTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teamApi.updateTeamMember,
    onSuccess: (data) => {
      // Invalidate and refetch team members
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
      
      toast.success(data.message || 'Team member updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update team member');
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teamApi.removeTeamMember,
    onSuccess: (data) => {
      // Invalidate and refetch team members
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
      
      toast.success(data.message || 'Team member removed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove team member');
    },
  });
}

export function useJoinTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teamApi.joinTeam,
    onSuccess: (data) => {
      // Invalidate team members and profile data
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      toast.success(data.message || 'Successfully joined team');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to join team');
    },
  });
}

// Export all hooks for convenience
export const useTeamApi = {
  useTeamMembers,
  useUpdateTeamMember,
  useRemoveTeamMember,
  useJoinTeam,
};